function getCellValue(cell) {
  return cell && typeof cell === "object" && Object.prototype.hasOwnProperty.call(cell, "value")
    ? cell.value
    : cell;
}

function normalizeHeader(cell) {
  return String(getCellValue(cell) ?? "").trim().toLowerCase();
}

function isPopulatedFieldValue(value) {
  const cellValue = getCellValue(value);
  return cellValue !== undefined
    && cellValue !== null
    && (typeof cellValue !== "string" || cellValue.trim().length > 0);
}

function normalizeComparableValue(value) {
  const cellValue = getCellValue(value);
  if (!isPopulatedFieldValue(cellValue)) return "";
  if (cellValue instanceof Date) {
    return Number.isNaN(cellValue.getTime()) ? String(cellValue) : cellValue.toISOString();
  }
  if (typeof cellValue === "object") {
    try {
      return JSON.stringify(cellValue);
    } catch {
      return String(cellValue);
    }
  }
  return String(cellValue);
}

function findEntityNameIndex(headerRow = []) {
  return headerRow.findIndex((cell) => {
    const header = normalizeHeader(cell);
    return header === "entity name" || header === "entityname";
  });
}

function findOutputColumns(headerRow = []) {
  return headerRow.reduce((columns, cell, index) => {
    const header = normalizeHeader(cell);
    if (header.startsWith("output ")) columns.push({ header, index });
    return columns;
  }, []);
}

function countPopulatedRowOutputs(row = [], outputColumns = []) {
  return outputColumns.reduce(
    (count, column) => count + (isPopulatedFieldValue(row[column.index]) ? 1 : 0),
    0
  );
}

function groupRowsByEntity(page = [], entityNameIndex) {
  const groupedRows = new Map();
  page.slice(1).forEach((row) => {
    if (!Array.isArray(row) || !isPopulatedFieldValue(row[entityNameIndex])) return;
    const entityName = normalizeComparableValue(row[entityNameIndex]).trim().toLowerCase();
    if (!groupedRows.has(entityName)) groupedRows.set(entityName, []);
    groupedRows.get(entityName).push(row);
  });
  return groupedRows;
}

function compareMatchedRows(extractedRow, qaRow, extractedOutputs, qaOutputs) {
  const counts = { added: 0, deleted: 0, modified: 0 };
  const qaOutputMap = new Map(qaOutputs.map((column) => [column.header, column]));
  const visitedHeaders = new Set();

  extractedOutputs.forEach((extractedColumn) => {
    visitedHeaders.add(extractedColumn.header);
    const qaColumn = qaOutputMap.get(extractedColumn.header);
    const extractedValue = extractedRow[extractedColumn.index];
    const qaValue = qaColumn ? qaRow[qaColumn.index] : undefined;
    const extractedPopulated = isPopulatedFieldValue(extractedValue);
    const qaPopulated = isPopulatedFieldValue(qaValue);

    if (!extractedPopulated && qaPopulated) counts.added += 1;
    else if (extractedPopulated && !qaPopulated) counts.deleted += 1;
    else if (
      extractedPopulated
      && qaPopulated
      && normalizeComparableValue(extractedValue) !== normalizeComparableValue(qaValue)
    ) counts.modified += 1;
  });

  qaOutputs.forEach((qaColumn) => {
    if (visitedHeaders.has(qaColumn.header)) return;
    if (isPopulatedFieldValue(qaRow[qaColumn.index])) counts.added += 1;
  });

  return counts;
}

function addCounts(target, source) {
  target.added += source.added;
  target.deleted += source.deleted;
  target.modified += source.modified;
}

export function countGenericQaFieldChanges(extractedWorkbook = [], qaWorkbook = []) {
  const counts = { added: 0, deleted: 0, modified: 0 };
  const pageCount = Math.max(extractedWorkbook?.length || 0, qaWorkbook?.length || 0);

  for (let pageIndex = 0; pageIndex < pageCount; pageIndex += 1) {
    const extractedPage = Array.isArray(extractedWorkbook?.[pageIndex]) ? extractedWorkbook[pageIndex] : [];
    const qaPage = Array.isArray(qaWorkbook?.[pageIndex]) ? qaWorkbook[pageIndex] : [];
    const extractedHeader = Array.isArray(extractedPage[0]) ? extractedPage[0] : [];
    const qaHeader = Array.isArray(qaPage[0]) ? qaPage[0] : [];
    const extractedEntityIndex = findEntityNameIndex(extractedHeader);
    const qaEntityIndex = findEntityNameIndex(qaHeader);
    if (extractedEntityIndex === -1 || qaEntityIndex === -1) continue;

    const extractedOutputs = findOutputColumns(extractedHeader);
    const qaOutputs = findOutputColumns(qaHeader);
    const extractedRows = groupRowsByEntity(extractedPage, extractedEntityIndex);
    const qaRows = groupRowsByEntity(qaPage, qaEntityIndex);
    const entityNames = new Set([...extractedRows.keys(), ...qaRows.keys()]);

    entityNames.forEach((entityName) => {
      const extractedEntityRows = extractedRows.get(entityName) || [];
      const qaEntityRows = qaRows.get(entityName) || [];
      const rowCount = Math.max(extractedEntityRows.length, qaEntityRows.length);

      for (let rowIndex = 0; rowIndex < rowCount; rowIndex += 1) {
        const extractedRow = extractedEntityRows[rowIndex];
        const qaRow = qaEntityRows[rowIndex];
        if (!extractedRow) counts.added += countPopulatedRowOutputs(qaRow, qaOutputs);
        else if (!qaRow) counts.deleted += countPopulatedRowOutputs(extractedRow, extractedOutputs);
        else addCounts(counts, compareMatchedRows(extractedRow, qaRow, extractedOutputs, qaOutputs));
      }
    });
  }

  return counts;
}

export { isPopulatedFieldValue };
